import React from 'react';
import PropTypes from 'prop-types';
import Transition from 'react-transition-group/Transition';
import noop from 'lodash-es/noop';

import Block from './Block';
import styles from '../styles';
import {
  runTransition,
  TRANSITION_PHASE_ENTER,
  TRANSITION_PHASE_EXIT,
} from '../../common/transitions';
import variables from '../../common/variables';
import ErrorBoundary from '../../common/components/ErrorBoundary';
import { getCSSGradientFromFabricGradient } from '../../common/gradients';
import { useDispatch } from 'react-redux';

const Slide = props => {
  const [data, setData] = React.useState(props.data);
  const slideRef = React.useRef();

  const [visible, setVisible] = React.useState(!props.scrollableViewer);
  const dispatch = useDispatch();

  if (props.scrollableViewer) {
    React.useEffect(() => {
      const isMostVisible = () => {
        const slideHeight = slideRef.current.offsetHeight;
        return (
          props.scrollTop >=
            (props.number - 2) * slideHeight + slideHeight / 2 &&
          props.scrollTop < (props.number - 1) * slideHeight + slideHeight / 2
        );
      };

      setVisible(isMostVisible());
    }, [props.scrollTop]);

    React.useEffect(() => {
      // TODO: investigate on iOS black screen are appearing when scrolling when we dispatch the visbility change
      if (visible) {
        dispatch({
          type: 'GO_TO_SLIDE',
          index: props.number - 1,
          slideChangedBy: 'MOUSE_WHEEL',
        });
      }
    }, [visible]);
  }

  React.useEffect(() => {
    setData(props.data);
  }, [props.data]);

  const doneCallback = React.useRef(null);
  const onTransitionEnter = node => {
    if (props.enterTransition) {
      window.requestAnimationFrame(() => {
        applyTween(node, props.enterTransition, TRANSITION_PHASE_ENTER);
      });
    } else if (doneCallback.current) {
      doneCallback.current();
    }
  };

  const onTransitionExit = node => {
    if (props.exitTransition) {
      window.requestAnimationFrame(() => {
        applyTween(node, props.exitTransition, TRANSITION_PHASE_EXIT);
      });
    } else if (doneCallback.current) {
      doneCallback.current();
    }
  };

  const onAddEndListener = (node, done) => {
    doneCallback.current = done;
  };

  const applyTween = (node, transition, phase) => {
    runTransition({
      element: node,
      transition,
      onStart: () => {
        props.onTransitionStart(phase);
      },
      onComplete: () => {
        if (doneCallback.current) {
          doneCallback.current();
        }
        props.onTransitionComplete(phase);
      },
    });
  };

  const { appInstanceId, globalScale, windowHasFocus } = props;

  let fillColor;
  let fillGradient;
  if (data.background && data.background.colorStops) {
    fillGradient = getCSSGradientFromFabricGradient(data.background);
  } else {
    fillColor = data.background || '#ffffff';
  }

  const slideStyles = {
    ...styles.slide,
    willChange: props.scrollableViewer ? 'none' : 'transform, filter',
    position: props.scrollableViewer ? 'relative' : 'absolute',
    backgroundColor: fillColor,
    backgroundImage: fillGradient,
    // borderRadius: isFullScreen || props.scrollableViewer ? '0' : `${5}px`,
    width: props.scrollableViewer
      ? `${Math.round(
          props.dimensions.containerWidth * props.dimensions.scale,
        )}px`
      : '100%',
    height: props.scrollableViewer
      ? `${Math.round(
          props.dimensions.containerHeight * props.dimensions.scale,
        )}px`
      : '100%',
    margin: 0,
    // transform: 'translateZ(0px)',
  };

  if (props.scrollableViewer) {
    return (
      <ErrorBoundary
        showAlert={false}
        message={variables.errors.slideCrash.message}
      >
        <div
          ref={slideRef}
          style={slideStyles}
          className={`slide-container slide${props.number}`}
        >
          {data.objects.map(block => (
            <Block
              key={block.canvasId}
              type={block.ludus ? block.ludus.type : 'unknown'}
              params={block}
              appInstanceId={appInstanceId}
              globalScale={globalScale}
              windowHasFocus={windowHasFocus}
              groupScaleX={1.0}
              groupScaleY={1.0}
              groupAngle={0}
              groupOpacity={1.0}
              currentSlideNumber={props.number}
              linksEnabled
              transitionStatus="entered"
              visible={visible || false}
            />
          ))}
        </div>
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary
      showAlert={false}
      message={variables.errors.slideCrash.message}
    >
      <Transition
        {...props}
        addEndListener={onAddEndListener}
        onEnter={onTransitionEnter}
        onExit={onTransitionExit}
        mountOnEnter
        unmountOnExit
      >
        {status => (
          <div style={slideStyles} className="slide-container">
            {data.objects.map(block => (
              <Block
                key={block.canvasId}
                type={block.ludus?.type || 'unknown'}
                params={block}
                appInstanceId={appInstanceId}
                globalScale={globalScale}
                windowHasFocus={windowHasFocus}
                groupScaleX={1.0}
                groupScaleY={1.0}
                groupAngle={0}
                groupOpacity={1.0}
                transitionStatus={status}
                currentSlideNumber={props.number}
                linksEnabled
                visible={visible}
              />
            ))}
          </div>
        )}
      </Transition>
    </ErrorBoundary>
  );
};

const transitionShape = PropTypes.shape({
  duration: PropTypes.number.isRequired,
  ease: PropTypes.shape({
    type: PropTypes.string.isRequired,
    curve: PropTypes.string.isRequired,
  }),
  fromParams: PropTypes.object.isRequired,
  toParams: PropTypes.object.isRequired,
});

Slide.propTypes = {
  appInstanceId: PropTypes.string.isRequired,
  data: PropTypes.shape({
    background: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    objects: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  globalScale: PropTypes.number.isRequired,
  windowHasFocus: PropTypes.bool.isRequired,
  enterTransition: transitionShape,
  exitTransition: transitionShape,
  onTransitionStart: PropTypes.func,
  onTransitionComplete: PropTypes.func,
  number: PropTypes.number.isRequired,
  isFullScreen: PropTypes.bool.isRequired,
  scrollableViewer: PropTypes.bool,
  scrollTop: PropTypes.number,
  dimensions: PropTypes.object,
};

Slide.defaultProps = {
  enterTransition: null,
  exitTransition: null,
  onTransitionStart: noop,
  onTransitionComplete: noop,
};

export default React.memo(Slide);
